import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { CheckoutStorageService } from '@bytel/checkout';

import { ENV_LIST, IInitialConfig } from '@interfaces/config.interface';

import { BehaviorSubject, Observable, ReplaySubject, Subject, delay } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { initialConfig } from '../../environments/environment';
import { BasicObject, DebugMode, IFormStatus } from '../interfaces/checkout.interface';
import { Mock } from '../interfaces/mock.interface';

// @dynamic
@Injectable({
    providedIn: 'root',
})
export class AppService {
    public skeletonObservable: Observable<boolean>;
    public showTicketObservable: Observable<boolean>;
    public debugMode: DebugMode = {
        active: false,
        open: true,
        mocks: false,
    };
    public debugSubject: Subject<DebugMode> = new Subject<DebugMode>();
    public modalSubject: Subject<boolean> = new Subject<boolean>();
    public resolversSubject: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    public mocks: BasicObject = {};
    public formStatusSubject: BehaviorSubject<IFormStatus> = new BehaviorSubject<IFormStatus>({
        loading: false,
        invalid: true,
    });
    public modalReady: boolean = false;

    protected formStatusUnsubscribe$: Subject<boolean> = new Subject<boolean>();

    private _storageDebugKey: string = 'test_mode';
    private _skeletonSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private _ticketSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly _skeletonId: string = 'skeleton-checkout';
    private readonly _ticketId: string = 'checkout-ticket';

    private _initialConfig: IInitialConfig;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private route: Router,
        private checkoutStorage: CheckoutStorageService,
    ) {
        this.skeletonObservable = this._skeletonSubject.asObservable();
        this.showTicketObservable = this._ticketSubject.asObservable();
        this._initialConfig = initialConfig();
        this.checkoutStorage.addLocalKey({
            [this._storageDebugKey]: [],
        });

        const debugMode = this.checkoutStorage.get(this._storageDebugKey);
        if (debugMode?.active && this.isTestEnv()) {
            this.debugMode = debugMode;
        }
    }

    public hideSkeleton(): void {
        this._skeletonSubject.next(false);
        const domSkeleton = this.document.getElementById(this._skeletonId);
        if (domSkeleton) {
            domSkeleton.remove();
        }
    }

    public shouldDisableTicket(state: boolean): void {
        this._ticketSubject.next(state);
    }

    public setDebugMode(active: boolean = true, open: boolean = true): void {
        this.debugMode.active = active;
        this.debugMode.open = open;
        this._setDebug();
    }

    public setMocksStatus(value: boolean = true): void {
        this.debugMode.mocks = value;
        this._setDebug();
    }

    public subscribeToFormStatusChanges(form: FormGroup): void {
        if (form) {
            this.formStatusSubject.next({ ...this.formStatusSubject.getValue(), invalid: !form.valid });
            form.statusChanges.pipe(delay(200), takeUntil(this.formStatusUnsubscribe$)).subscribe(() => {
                this.formStatusSubject.next({ ...this.formStatusSubject.getValue(), invalid: form.invalid });
            });
        }
    }

    public unsubscribeFromFormStatusChanges(): void {
        this.formStatusUnsubscribe$.next(null);
    }

    public setMocks(url: string, mock: Mock, multiMode: boolean = false): void {
        if (!this.mocks[url]) {
            this.mocks[url] = [];
        }
        if (multiMode) {
            this.mocks[url].push(mock);
        } else {
            this.mocks[url] = [mock];
        }
    }

    /**
     * Navigate to specific URL
     * Use params to add query parameters
     * @param url url to navigate
     * @param params query parameters
     * @param isExternal True if href is external to avoid 404
     */
    public navigateTo(url: string, params: BasicObject = null, isExternal: boolean = false): void {
        if (isExternal) {
            this.document.defaultView.location.href = url;
            return;
        }

        url = url.replace(this.document.location.origin, '');
        if (params) {
            this.route.navigate([url], { queryParams: params }).catch(() => {
                this.document.defaultView.location.href = url + this._transformParams(params);
            });
        } else {
            this.route.navigateByUrl(url).catch(() => {
                this.document.defaultView.location.href = url;
            });
        }
    }
    public isTestEnv(): boolean {
        const testEnv = [ENV_LIST.LOCAL, ENV_LIST.TEST];
        return testEnv.includes(this._initialConfig.environment);
    }

    private _setDebug(): void {
        this.checkoutStorage.set(this._storageDebugKey, this.debugMode);
        this.debugSubject.next(this.debugMode);
    }

    private _transformParams(params: BasicObject): string {
        let stringParam = '';

        if (params) {
            stringParam =
                '?' +
                Object.keys(params)
                    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
                    .join('&');
        }

        return stringParam;
    }
}
